// Forked into ../redesign/SearchDropdown.tsx (the dropdown shell) and
// ../redesign/Results/ResultsSection.tsx (the result groups) — keep in sync until phase_0 cleanup.
import React from 'react';
import styled from 'styled-components';
import { token } from '@bynder/design-system';
import { isModule, isView } from '../../../helpers/windowLocation';
import { isColdfusionID } from '../../../helpers/id';
import { useSpotlightCapabilities } from '../hooks/useSpotlightCapabilities';
import { GroupAssets } from './GroupAssets';
import { GroupCollections } from './GroupCollections';
import { GroupTags } from './GroupTags/GroupTags';
import { GroupGuidelines } from './GroupGuidelines';
import { GroupMetaproperties } from './GroupMetaproperties';
import {
	getResponsiveSpotlightWidth,
	ORDER_OF_SECTIONS_IN_SPOTLIGHT_DROPDOWN,
	SEARCH_MIN_CHARS,
} from '../constants';
import { HELP_FOOTER_HEIGHT, HelpFooter } from '../Help/HelpFooter';
import { useShowHelpFooter } from '../Help/useShowHelpFooter';
import { NoResults } from './NoResults';
import { ClassicBannerNLS } from './StyledComponents';
import { getSpotlightDropdownDimensions } from './utils';
import { SearchOptions } from '../SearchOptions/SearchOptions';

import { RecentSearches } from '../RecentSearches/RecentSearches';
import {
	useSearchMode,
	useSpotlightDropdownContext,
	useSearch,
	useSearchScope,
} from '../SpotlightState';

interface Props {
	showMainSection: boolean;
	showNLSBanner: boolean;
}

export function ResultsDropdown({ showMainSection, showNLSBanner }: Props) {
	const { selectedSearchOptions } = useSearchScope();
	const { isLoadingResults, searchTerm, searchResults } = useSearch();
	const { refInput } = useSpotlightDropdownContext();
	const { isPerformingNLSSearch } = useSearchMode();
	const { canShowRecentSearches } = useSpotlightCapabilities();

	const isCollection = isCollectionView();
	const hasAssets = searchResults.assets.items?.length > 0;
	const hasTags = searchResults.tags?.length > 0;
	const hasCollections = searchResults.collections.items?.length > 0;
	const hasGuidelines = searchResults.guidelines.items?.length > 0;
	const hasMetaproperties =
		searchResults.metaproperties && searchResults.metaproperties.length > 0;
	const hasSelectedSearchOptions = selectedSearchOptions.length > 0;

	const hasSearchableTerm = searchTerm.length >= SEARCH_MIN_CHARS;
	const showRecentSearches =
		canShowRecentSearches && searchTerm.length === 0 && selectedSearchOptions.length === 0;
	const showHelpFooter = useShowHelpFooter();

	const isOpen = showMainSection || showNLSBanner;

	const showSearchOptions = !isPerformingNLSSearch;
	const showAssetsSection = hasAssets && hasSearchableTerm;
	const showCollectionsSection =
		!isCollection && hasCollections && hasSearchableTerm && !hasSelectedSearchOptions;
	const showGuidelinesSection = hasGuidelines && hasSearchableTerm && !hasSelectedSearchOptions;
	const showMetapropertiesSection =
		hasMetaproperties && hasSearchableTerm && !hasSelectedSearchOptions;
	const showTagsSection = hasTags && hasSearchableTerm && !hasSelectedSearchOptions;
	const showsAnySection =
		showAssetsSection ||
		showCollectionsSection ||
		showGuidelinesSection ||
		showMetapropertiesSection ||
		showTagsSection;
	const showNoResults = !isLoadingResults && hasSearchableTerm && !showsAnySection;
	const sectionsToDisplayInDropdown: { [key: string]: React.ReactNode | false } = {
		assets: showAssetsSection && <GroupAssets assetResults={searchResults.assets} />,
		collections: showCollectionsSection && (
			<GroupCollections collectionResults={searchResults.collections} />
		),
		guidelines: showGuidelinesSection && (
			<GroupGuidelines guidelines={searchResults.guidelines.items} />
		),
		metaproperties:
			showMetapropertiesSection &&
			searchResults.metaproperties.map((metaproperty) => (
				<GroupMetaproperties metaproperty={metaproperty} key={metaproperty.name} />
			)),
		tags: showTagsSection && <GroupTags tags={searchResults.tags} />,
	};

	const { dropdownMaxHeight, innerDivMaxHeight, dropdownTop } = getSpotlightDropdownDimensions(
		refInput,
		showNLSBanner,
		showSearchOptions,
	);

	return (
		<div id="spotlightContainer">
			<StyledOuterDiv
				data-keyword={searchTerm}
				$isOpen={isOpen}
				$top={dropdownTop}
				$maxHeight={dropdownMaxHeight}
				$isPerformingNLSSearch={isPerformingNLSSearch}
			>
				<ul>
					{showNLSBanner && <ClassicBannerNLS />}
					{showSearchOptions && <SearchOptions />}
					<StyledInnerDiv $showHelpFooter={showHelpFooter} $maxHeight={innerDivMaxHeight}>
						{showRecentSearches && <RecentSearches />}
						<div
							style={{
								display: `${isPerformingNLSSearch ? 'none' : 'inherit'}`,
							}}
						>
							{ORDER_OF_SECTIONS_IN_SPOTLIGHT_DROPDOWN.map((sectionName) => (
								<React.Fragment key={sectionName}>
									{sectionsToDisplayInDropdown[sectionName]}
								</React.Fragment>
							))}
							{showNoResults && <NoResults />}
						</div>
						{showHelpFooter && <HelpFooter showSearchKeyboardShortcut={false} />}
					</StyledInnerDiv>
				</ul>
			</StyledOuterDiv>
		</div>
	);
}

const StyledOuterDiv = styled.div<{
	$isOpen: boolean;
	$top: number;
	$maxHeight: number;
	$isPerformingNLSSearch: boolean;
}>`
	display: ${({ $isOpen }) => ($isOpen ? 'inherit' : 'none')};
	${getResponsiveSpotlightWidth()}
	z-index: 99999;
	position: absolute;
	top: ${({ $top }) => $top}px;
	padding: 0;
	border: 1px solid transparent;
	border-color: ${({ $isPerformingNLSSearch }) =>
		$isPerformingNLSSearch ? token.purple500 : '#d9dee1'};
	border-radius: ${token.radiusMedium};
	box-sizing: border-box !important;
	overflow: hidden;
	overscroll-behavior: contain;
	background-color: ${token.white};
	box-shadow: 0 1px 6px 0 rgba(0, 0, 0, 0.05), 0px 2px 8px -1px #03102626;
	max-height: ${({ $maxHeight }) => $maxHeight}px;

	& * {
		box-sizing: border-box !important;
	}
`;

const StyledInnerDiv = styled.div<{
	$showHelpFooter: boolean;
	$maxHeight: number;
}>`
	padding-bottom: ${({ $showHelpFooter }) => ($showHelpFooter ? `${HELP_FOOTER_HEIGHT}px` : '0')};
	overflow-y: auto;
	overscroll-behavior: contain;
	max-height: ${({ $maxHeight }) => $maxHeight}px;

	&:first-child {
		padding-top: 8px;
	}

	> :nth-last-child(${({ $showHelpFooter }) => ($showHelpFooter ? '2' : '1')}) {
		border-bottom: none !important;
	}
`;

const isCollectionView = () => {
	if (!isModule('collections') || !isView('view')) {
		return false;
	}

	const id = window.location.pathname.split('/')[3] || '';

	if (isColdfusionID(id)) {
		return true;
	}

	return false;
};
